import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js/pure';
import siteSetting from '../../../../config/env';
import { paymentService } from '../../../../_services/payment.service';
import { hairdresserService } from '../../../../_services/hairdresser.service';
import { marketingService } from '../../../../_services/marketing.service';
import { STRIPE_LINK_DISABLED, refreshStripeStatus, useStripeStatus } from '../../../settings/stripeStatus';

/**
 * Getting paid: everything the chapter reads and writes, in one place.
 *
 * The server is the truth for every payments tick, so each visit derives it
 * afresh: Stripe and Beu's approval from getBank, card readers from
 * cardReaderList, the till's methods from payment-methods and the billing
 * card from checkCardAdded. Setup state keeps only what the server can't
 * tell (a reader ordered, a "later", when Stripe was opened).
 *
 * Two things leave the app: Stripe Connect and Stripe Checkout (the billing
 * card). These helpers only fetch the link or the session. The chapter saves
 * its place first and then navigates, so a return lands on the same step.
 *
 * Shapes and quirks: setup-build/api-payments.md (sections cited below).
 */

/* ---------------- responses, read defensively ---------------- */

const ok = (res) => !!(res && res.data && res.data.statusCode === 1);

// Reason strings reach the owner, so they lose any dashes on the way.
const tidy = (msg) => String(msg || '').replace(/\s*[\u2013\u2014]\s*/g, ', ').replace(/\s+/g, ' ').trim();

function reason(d) {
  if (!d) return '';
  const e = d.error;
  if (typeof e === 'string') return tidy(e);
  return tidy((e && (e.responseMessage || e.message || (e.errors && e.errors.responseMessage))) || d.message || '');
}
const refused = (res) => reason(res && res.data);           // HTTP 200 with statusCode 0
const failed = (err) => reason(err && err.response && err.response.data); // a rejected request

function plainError(message) {
  const e = new Error(message);
  e.plain = true;
  return e;
}

export const titleCase = (s) => String(s || '').toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());
export const firstWord = (name) => String(name || '').trim().split(/\s+/)[0] || '';

/** True when `iso` is within the last `ms` (twelve hours by default). */
export const isRecent = (iso, ms = 12 * 36e5) => {
  const t = Date.parse(iso || '');
  return Number.isFinite(t) && Date.now() - t < ms;
};

/** True when timestamp `at` is later than `iso`. */
export const after = (at, iso) => {
  const t = Date.parse(iso || '');
  return Number.isFinite(t) && Number(at) > t;
};

/**
 * Before leaving the app: SetupContext writes this browser at once and the
 * server 450ms later, so wait long enough for that write to go out.
 */
export const settle = (since, ms = 1100) => new Promise((r) => setTimeout(r, Math.max(0, ms - (Date.now() - since))));

/* ---------------- Stripe and Beu's approval (map sections 1 to 5) ---------------- */

/** Money can move, or Stripe is finishing its checks with nothing left to do. */
export const stripeLive = (s) => !!s && (s.key === 'active' || s.key === 'verifying');

// What each demo state's sample account says about charges (stripeStatus.js demoSample).
const DEMO_CHARGES = { issue: true, verifying: true, paused: false, restricted: false, not_connected: false, beu_review: false };

/**
 * Can a client's card be taken right now? 'live' | 'off' | 'unknown'.
 *
 * Read from the Stripe account's own charges_enabled, never from the derived
 * key: a live salon with something currently due on Stripe ('issue'), or
 * payouts paused ('restricted'), still takes cards. 'unknown' means we don't
 * know (still loading, Stripe didn't answer, no account object to read), and
 * nothing that depends on it may act as if cards were off: no stripping of
 * live deposits, no pausing charges. Say so and offer to check again.
 *
 *   s        a status from useStripeStatus / useStripeLive (or one carrying .cards)
 *   account  the Stripe account getBank returned (redux bankDetails)
 */
export function cardsState(s, account) {
  if (!s || s.loading) return 'unknown';
  if (s.cards === 'live' || s.cards === 'off' || s.cards === 'unknown') return s.cards;
  if (s.demo) {
    const c = DEMO_CHARGES[s.demo];
    return c === undefined ? 'unknown' : (c ? 'live' : 'off');
  }
  if (s.key === 'unknown') return 'unknown';
  const a = account && typeof account === 'object' ? account : null;
  if (a && typeof a.charges_enabled === 'boolean') return a.charges_enabled ? 'live' : 'off';
  // No account to read: that's only an answer when there's plainly no account yet.
  if (s.key === 'not_connected' || (s.key === 'beu_review' && !s.bank)) return 'off';
  return 'unknown';
}

/**
 * A client's card can be taken (charges_enabled). What deposits, charges and
 * the ready check wait for. False for 'unknown' too, so callers that act on
 * "not live" must check cardsState(...) === 'off' instead.
 */
export const cardsLive = (s, account) => cardsState(s, account) === 'live';

/* The last Stripe status a setup screen saw, with its cards answer, so the
   ready check can use the shared look instead of calling getBank itself
   (getBank lazily creates a Stripe Connect account). */
let lastSeen = null; // { status, at }
function publishStripe(status) {
  if (status && !status.demo) lastSeen = { status, at: Date.now() };
}

/** The shared look if one landed within `maxAge` ms, or null. */
export function sharedStripeStatus(maxAge = 10 * 60e3) {
  return lastSeen && Date.now() - lastSeen.at < maxAge ? lastSeen.status : null;
}

/** The hosted onboarding link is only offered when there's something to do on Stripe. */
export const canOpenStripe = (s) => !!s && !s.demo && (s.key === 'not_connected' || s.key === 'issue');

const merchantFrom = (d) => {
  if (!d || !Object.prototype.hasOwnProperty.call(d, 'merchantStatus')) return null;
  return d.merchantStatus === 'pending' || d.merchantStatus === 'flagged' ? d.merchantStatus : 'approved';
};

/**
 * Beu's approval: 'pending' | 'flagged' | 'approved', or null when it can't
 * be told. The Stripe status wins (it carries the demo states too), then the
 * payment-methods read.
 */
export function approvalOf(s, fromMethods) {
  if (s && s.key === 'beu_review') return (fromMethods === 'flagged' || s.chip === 'Being re-checked') ? 'flagged' : 'pending';
  if (s && s.key !== 'unknown') return 'approved';
  return fromMethods || null;
}

// Mounting a fresh subscriber is what makes useStripeStatus fetch again
// after refreshStripeStatus() has cleared the shared copy (map section 4).
function StripeFetch() {
  useStripeStatus(null, true);
  return null;
}

/**
 * The Stripe status, kept fresh with the map's refresh recipe so every other
 * Stripe surface in the app (the alert bar, the bell) stays in step:
 *   status     the last status that finished loading (null before the first),
 *              carrying .cards: 'live' | 'off' | 'unknown' (cardsState)
 *   cards      the same answer; 'unknown' until a look has landed
 *   checking   a look is in flight
 *   checkedAt  when the last look landed (ms)
 *   recheck()  look again
 *   fetcher    render this; it is the remounted subscriber that fetches
 *
 * It looks afresh on arrival, so a screen never acts on an hours-old copy.
 */
export function useStripeLive(enabled = true) {
  const raw = useStripeStatus(null, enabled);
  const account = useSelector((st) => st.bankDetails);
  const [probe, setProbe] = useState(0);
  const [checkedAt, setCheckedAt] = useState(0);
  const last = useRef(null);
  // The status and the account it came from are read together, so the
  // cards answer always matches the status beside it.
  const status = raw.loading ? raw : { ...raw, cards: cardsState(raw, account) };
  if (!status.loading) last.current = status;
  const wasLoading = useRef(status.loading);
  const loadedOnArrival = useRef(!status.loading);

  const recheck = useCallback(() => {
    refreshStripeStatus();
    setProbe((n) => n + 1);
  }, []);

  // A fresh look on arrival: the shared copy can be hours old. If it is
  // still loading, the request in flight is fresh already.
  useEffect(() => {
    if (enabled && loadedOnArrival.current) recheck();
  }, [enabled, recheck]);

  useEffect(() => {
    if (wasLoading.current && !status.loading) setCheckedAt(Date.now());
    wasLoading.current = status.loading;
  }, [status.loading]);

  const shown = status.loading ? last.current : status;
  const shownKey = shown ? `${shown.key}|${shown.cards}|${shown.demo || ''}` : '';
  // Shared only once a look has landed here, never the copy found on arrival.
  useEffect(() => {
    if (enabled && shown && !status.loading && checkedAt) publishStripe(shown);
  }, [enabled, shownKey, status.loading, checkedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    status: shown,
    cards: shown ? shown.cards : 'unknown',
    checking: !!status.loading,
    // A demo state never loads, so it always counts as just checked.
    checkedAt: status.demo ? Date.now() : checkedAt,
    recheck,
    fetcher: enabled && probe > 0 ? React.createElement(StripeFetch, { key: probe }) : null,
  };
}

/**
 * For the ready check: the shared Stripe look, fresh, as a promise it can
 * wait on, so the check never calls getBank itself.
 *
 *   const stripeCheck = useStripeCheck(!noCards);
 *   runReadyCheck({ ...args, stripe: stripeCheck.read });
 *   // and render {stripeCheck.fetcher}
 *
 * read() resolves with the next status that lands (a look in flight is
 * waited on, not repeated), carrying .cards.
 */
export function useStripeCheck(enabled = true) {
  const live = useStripeLive(enabled);
  const waiting = useRef([]);
  const latest = useRef(live);
  latest.current = live;
  useEffect(() => {
    if (live.checking || !live.checkedAt || !waiting.current.length) return;
    const list = waiting.current;
    waiting.current = [];
    list.forEach((resolve) => resolve(latest.current.status));
  }, [live.checking, live.checkedAt]);
  const read = useCallback(() => new Promise((resolve) => {
    waiting.current.push(resolve);
    if (!latest.current.checking) latest.current.recheck();
  }), []);
  return { ...live, read };
}

/**
 * The Stripe-hosted onboarding link, made on tap (links are single-use and
 * short-lived). Called directly rather than through openStripeFix, so a
 * failure can be shown inline instead of a toast and a jump to Settings.
 * Throws a plain message; `approvalGate` is set when Beu's approval blocks it.
 */
export async function stripeLink() {
  // The same incident switch Settings obeys.
  if (STRIPE_LINK_DISABLED) throw plainError('Changing your Stripe details is temporarily unavailable. Please contact support.');
  let res;
  try {
    res = await paymentService.bankLink({});
  } catch (err) {
    throw plainError(failed(err) || 'I couldn’t open Stripe just now. Please try again.');
  }
  const d = (res && res.data) || {};
  const url = ok(res) && d.responseData && d.responseData.result && d.responseData.result.url;
  if (url) return url;
  const msg = refused(res);
  const e = plainError(msg || 'I couldn’t open Stripe just now. Please try again.');
  e.approvalGate = /approval|review|awaiting/i.test(msg);
  throw e;
}

/** "Paid out every day", from the Stripe account's payout schedule. */
export function payoutLine(account) {
  const s = account && account.settings && account.settings.payouts && account.settings.payouts.schedule;
  if (!s || !s.interval) return '';
  if (s.interval === 'daily') return 'Paid out every day';
  if (s.interval === 'weekly') return s.weekly_anchor ? `Paid out every week, on ${titleCase(s.weekly_anchor)}s` : 'Paid out every week';
  if (s.interval === 'monthly') return 'Paid out once a month';
  if (s.interval === 'manual') return 'Paid out when you ask';
  return '';
}

/**
 * Beu's approval alone, from payment-methods: no Stripe call, so it suits a
 * gentle poll while approval is pending (map section 3, gotcha 3). Never
 * rejects: null when it can't tell.
 */
export async function readApproval() {
  try {
    const res = await hairdresserService.getPaymentMethods({});
    const d = (res && res.data && (res.data.responseData || res.data.data)) || {};
    return ok(res) ? merchantFrom(d) : null;
  } catch (e) {
    return null;
  }
}

/* ---------------- ways to pay at the till (map sections 11 and 12) ---------------- */

export const BEU_METHODS = [
  { key: 'beuCardReaders', label: 'Card reader', hint: 'Contactless, chip and PIN at the desk', fee: '1.25% + 20p' },
  { key: 'paymentLink', label: 'Quick Pay link and QR code', hint: 'The client pays on their own phone', fee: '1.25% + 20p' },
  { key: 'cardOnFile', label: 'Card on file', hint: 'Charge the card a client saved when booking', fee: '1.25% + 20p' },
  { key: 'klarna', label: 'Klarna', hint: 'The client pays in instalments', fee: '5.99% + 35p' },
  { key: 'clearPay', label: 'Clearpay', hint: 'The client pays in instalments', fee: '6.99% + 30p' },
];

export const OWN_METHODS = [
  { key: 'cash', label: 'Cash' },
  { key: 'card', label: 'Card on your own machine', hint: 'Taken outside Beu and recorded at the till' },
  { key: 'vouchers', label: 'Vouchers', hint: 'Gift cards and vouchers you’ve sold' },
  { key: 'other', label: 'Other', hint: 'Anything else, like a bank transfer' },
];

const METHOD_KEYS = BEU_METHODS.concat(OWN_METHODS).map((m) => m.key);
// Everything the backend masks off while Beu's approval is pending (tapToPay too).
const BEU_KEYS = BEU_METHODS.map((m) => m.key).concat(['tapToPay']);

// The till treats a missing key as switched on (PaymentScreen.js), so the
// switches here say the same. Tap to Pay is a pilot: it goes back exactly as
// it came and is never added.
export const methodOn = (map, key) => !!map && map[key] !== false;
const wholeMap = (map) => METHOD_KEYS.reduce((out, k) => ({ ...out, [k]: methodOn(map, k) }), { ...(map || {}) });

/**
 * The signed-in person's own till methods. A switch saves the moment it
 * moves: one save at a time, always the whole map, always the latest
 * switches. A failed save puts the switches back to what the server holds.
 */
export function usePaymentMethods(enabled = true) {
  const [map, setMap] = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);          // { key, at } after a save lands
  const [saveError, setSaveError] = useState(null);  // { message, attempt }
  const alive = useRef(true);
  const confirmed = useRef(null); // what the server last accepted
  const latest = useRef(null);    // what the switches show now
  const lastKey = useRef('');
  const sending = useRef(false);
  const again = useRef(false);
  const flush = useRef(null);
  const masked = useRef(false);   // Beu hasn't approved yet: the card methods read back as off
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    return hairdresserService.getPaymentMethods({})
      .then((res) => {
        if (!alive.current) return;
        if (!ok(res)) { setError(refused(res) || 'I couldn’t load your payment methods just now.'); return; }
        const d = res.data.responseData || res.data.data || {};
        const m = d.paymentMethods && typeof d.paymentMethods === 'object' ? d.paymentMethods : {};
        confirmed.current = m;
        latest.current = m;
        setMap(m);
        const who = merchantFrom(d);
        masked.current = who === 'pending' || who === 'flagged';
        setMerchant(who);
      })
      .catch((err) => { if (alive.current) setError(failed(err) || 'I couldn’t load your payment methods just now.'); })
      .then(() => { if (alive.current) setLoading(false); });
  }, []);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);

  flush.current = () => {
    if (sending.current) { again.current = true; return; }
    sending.current = true;
    again.current = false;
    const body = latest.current;
    // While Beu's approval is pending the card methods are masked off in the
    // read, so they're never sent back: the stored ones stay as they are.
    const send = masked.current
      ? Object.keys(body || {}).filter((k) => !BEU_KEYS.includes(k)).reduce((o, k) => ({ ...o, [k]: body[k] }), {})
      : body;
    hairdresserService.updatePaymentMethods({ paymentMethods: send })
      .then((res) => {
        if (!ok(res)) throw plainError(refused(res));
        confirmed.current = body;
        sending.current = false;
        if (again.current) { flush.current(); return; }
        if (!alive.current) return;
        setSaving(false);
        setSaved({ key: lastKey.current, at: Date.now() });
      })
      .catch((err) => {
        sending.current = false;
        again.current = false;
        const attempt = latest.current;
        latest.current = confirmed.current;
        if (!alive.current) return;
        setMap(confirmed.current);
        setSaving(false);
        setSaveError({ message: (err && err.plain ? err.message : failed(err)) || '', attempt });
      });
  };

  const toggle = useCallback((key, on) => {
    const next = wholeMap({ ...(latest.current || {}), [key]: on });
    latest.current = next;
    lastKey.current = key;
    setMap(next);
    setSaving(true);
    setSaveError(null);
    flush.current();
  }, []);

  const retry = useCallback(() => {
    if (!saveError || !saveError.attempt) return;
    latest.current = saveError.attempt;
    setMap(saveError.attempt);
    setSaving(true);
    setSaveError(null);
    flush.current();
  }, [saveError]);

  // masked: the Beu card methods in `map` are the read's mask, not anyone's choice.
  return { map, merchant, masked: merchant === 'pending' || merchant === 'flagged', loading, error, reload: load, toggle, saving, saved, saveError, retry };
}

/* ---------------- card readers (map sections 7 to 9) ---------------- */

// The Stripe Payment Link Settings > Payments uses (CardReaders.js:515). It is
// LIVE in every build: completing it buys a real reader.
export const READER_LINK = 'https://buy.stripe.com/6oE29f3S432E4mcbIL';

const DEVICE_NAMES = {
  bbpos_wisepos_e: 'BBPOS WisePOS E',
  simulated_wisepos_e: 'Simulated WisePOS E',
  stripe_s700: 'Stripe Reader S700',
  stripe_m2: 'Stripe Reader M2',
  bbpos_chipper2x: 'BBPOS Chipper 2X BT',
  bbpos_wisepad3: 'BBPOS WisePad 3',
};
export const deviceName = (type) => DEVICE_NAMES[type] || titleCase(String(type || 'Card reader').replace(/_/g, ' '));

/**
 * The readers registered to this person's Stripe account. Only asked once
 * Stripe is connected: there are no readers without an account.
 */
export function useReaders(enabled) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pairing, setPairing] = useState(false);
  const [pairError, setPairError] = useState('');
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    return paymentService.cardReaderList({})
      .then((res) => {
        if (!alive.current) return;
        if (!ok(res)) { setError(refused(res) || 'I couldn’t load your card readers just now.'); return; }
        const r = res.data.responseData && res.data.responseData.result;
        setList(r && Array.isArray(r.data) ? r.data : []);
      })
      .catch((err) => { if (alive.current) setError(failed(err) || 'I couldn’t load your card readers just now.'); })
      .then(() => { if (alive.current) setLoading(false); });
  }, []);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);

  /** Registers a reader by its three-word code. Resolves true once it's on the list. */
  const pair = useCallback(async (code, label) => {
    setPairing(true);
    setPairError('');
    try {
      const res = await paymentService.addTerminal({ registrationCode: code, label });
      if (!ok(res)) throw plainError(refused(res) || 'That code didn’t work. Check the three words on the reader and try again.');
      await load();
      return true;
    } catch (err) {
      if (alive.current) setPairError(err && err.plain ? err.message : (failed(err) || 'I couldn’t reach Stripe just now. Please try again.'));
      return false;
    } finally {
      if (alive.current) setPairing(false);
    }
  }, [load]);

  return { list, loading, error, reload: load, pair, pairing, pairError };
}

/* ---------------- the billing card (map sections 14 and 15) ---------------- */

const BRANDS = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', american_express: 'Amex', discover: 'Discover', diners: 'Diners', jcb: 'JCB', unionpay: 'UnionPay', maestro: 'Maestro' };

/** A Onelink (Stripe 'link') method, or a card that hasn't expired (Billing.js:27-34). */
export function usableCard(pm) {
  if (!pm) return false;
  if (pm.type === 'link') return true;
  const c = pm.card;
  if (!c) return false;
  const y = Number(c.exp_year);
  const m = Number(c.exp_month);
  if (!y || !m) return true;
  const now = new Date();
  return y > now.getFullYear() || (y === now.getFullYear() && m >= now.getMonth() + 1);
}

/** { name: 'Visa ending 4242', detail: 'Expires 08/27' } */
export function cardWords(pm) {
  if (!pm) return { name: '', detail: '' };
  if (pm.type === 'link' || !pm.card) return { name: 'Onelink', detail: (pm.link && pm.link.email) || 'Saved with Stripe' };
  const c = pm.card;
  const brand = BRANDS[String(c.brand || '').toLowerCase()] || titleCase(c.brand || 'Card');
  const exp = c.exp_month && c.exp_year ? `${String(c.exp_month).padStart(2, '0')}/${String(c.exp_year).slice(-2)}` : '';
  return { name: `${brand} ending ${c.last4 || ''}`.trim(), detail: exp ? `Expires ${exp}` : '' };
}

function bestCard(cards, defaultId) {
  const list = cards || [];
  const byDefault = list.find((c) => c && c.id === defaultId) || null;
  return [byDefault].concat(list).find((c) => c && usableCard(c)) || byDefault || list[0] || null;
}

/**
 * The card Beu bills its own charges to (texts beyond the free allowance,
 * the monthly bill). statusCode 0 means both "no Stripe customer yet" and a
 * refusal, so it reads as no card, as it does everywhere else in the app.
 */
export function useBillingCards(enabled = true) {
  const [state, setState] = useState({ cards: null, defaultId: null, loading: !!enabled, error: '', checkedAt: 0 });
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    return paymentService.checkCardAdded({})
      .then((res) => {
        if (!alive.current) return;
        const d = ok(res) ? (res.data.responseData || {}) : {};
        const cards = d.result && Array.isArray(d.result.data) ? d.result.data : [];
        const dp = d.stripeCustomer && d.stripeCustomer.invoice_settings && d.stripeCustomer.invoice_settings.default_payment_method;
        const defaultId = typeof dp === 'string' ? dp : (dp && dp.id) || null;
        setState({ cards, defaultId, loading: false, error: '', checkedAt: Date.now() });
      })
      .catch((err) => {
        if (!alive.current) return;
        setState((s) => ({ ...s, loading: false, error: failed(err) || 'I couldn’t check for a saved card just now.', checkedAt: Date.now() }));
      });
  }, []);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);

  const best = bestCard(state.cards, state.defaultId);
  return { ...state, reload: load, best, usable: usableCard(best) };
}

// Stripe.js is only fetched when a card is actually being added.
let stripeJs = null;
function stripeClient() {
  if (!stripeJs) stripeJs = loadStripe(siteSetting.stripeKey).catch((e) => { stripeJs = null; throw e; });
  return stripeJs;
}

/** A Stripe Checkout session for adding a card. Adding one charges nothing. */
export async function cardCheckoutSession() {
  let res;
  try {
    res = await paymentService.paymentMethodAdd({});
  } catch (err) {
    throw plainError(failed(err) || 'I couldn’t reach Stripe just now. Please try again.');
  }
  const d = (res && res.data) || {};
  const id = ok(res) && d.responseData && d.responseData.result && d.responseData.result.id;
  if (!id) throw plainError(refused(res) || 'Stripe didn’t open just now. Please try again.');
  return id;
}

/**
 * Leaves for Stripe Checkout. `ready()` is asked once Stripe's script has
 * loaded, right before leaving: false stops there and resolves false.
 * Otherwise it only resolves if the redirect didn't happen.
 */
export async function goToCardCheckout(sessionId, ready = () => true) {
  let stripe = null;
  try {
    stripe = await stripeClient();
  } catch (e) {
    stripe = null;
  }
  if (!stripe) throw plainError('Stripe didn’t load. Check your connection and try again.');
  if (!ready()) return false;
  const r = await stripe.redirectToCheckout({ sessionId });
  if (r && r.error) throw plainError(tidy(r.error.message) || 'Stripe didn’t open just now. Please try again.');
  return true;
}

/* ---------------- the team's self-employed stylists (api-team section 1) ---------------- */

/**
 * The self-employed stylists on the owner's team, with each one's salon
 * cut. The signed-in person is left out (a solo self-employed owner has the
 * same flags as a stylist).
 */
export function useSelfEmployed(enabled) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState('');
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(() => {
    const me = String(localStorage.getItem('userId') || '');
    setLoading(true);
    setError('');
    return hairdresserService.staffRotaV2({ hairdresserId: me, searchKey: '', page: 1, listType: 1 })
      .then((res) => {
        if (!alive.current) return;
        if (!ok(res)) { setError(refused(res) || 'I couldn’t load your team just now.'); return; }
        const rows = (res.data.responseData && res.data.responseData.result) || [];
        setList(rows
          .filter(Boolean)
          .filter((r) => r.name !== 'Unknown' && r.email !== 'Unknown' && String(r._id) !== me)
          .filter((r) => r.hairdresserInfo && Number(r.hairdresserInfo.employmentTypeId) === 2 && Number(r.hairdresserInfo.isSalonOwner) !== 1)
          .map((r) => ({
            id: String(r._id),
            name: r.name || 'Stylist',
            photo: (r.userImage && r.userImage.circleImage) || '',
            cut: Number(r.hairdresserInfo.salonCommissionRate) || 0,
          })));
      })
      .catch((err) => { if (alive.current) setError(failed(err) || 'I couldn’t load your team just now.'); })
      .then(() => { if (alive.current) setLoading(false); });
  }, []);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);

  return { list, loading, error, reload: load };
}

/* ---------------- does the salon cover this stylist's texts (api-money-rules 14) ---------------- */

/**
 * For an invited self-employed stylist: 'covered' when the salon manages
 * their messages (both consents on, so its allowance covers their texts),
 * 'offered' when only the salon has said yes, 'none', or null when it
 * can't be told.
 */
export function useTextsCover(enabled, user) {
  const [cover, setCover] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const info = (user && user.userInfo) || {};
  const h = info.hairdresser || {};
  const ids = [localStorage.getItem('userId'), info.userId, info._id, h.userId, h._id].filter(Boolean).map(String).join('|');

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    const mine = ids.split('|');
    marketingService.getMarketingSetting()
      .then((res) => {
        if (!live) return;
        if (!ok(res)) { setCover(null); return; }
        const rows = (res.data.responseData && res.data.responseData.result) || [];
        // Only this stylist's own row answers it: another stylist's consent says nothing about theirs.
        const row = rows.find((r) => r && mine.includes(String(r.hairdresserId))) || null;
        if (!row) { setCover('none'); return; }
        const salon = Number(row.salonConsent) === 1;
        const self = Number(row.selfEmpConsent) === 1;
        setCover(salon && self ? 'covered' : salon ? 'offered' : 'none');
      })
      .catch(() => { if (live) setCover(null); })
      .then(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [enabled, ids]);

  return { cover, loading };
}

// Beu's fee for no-show protection, said one way everywhere in setup: per
// protected booking, waived when the client pays through Beu (the same rule
// as Settings > Booking link).
export const NO_SHOW_FEE_LINE = 'When not processed through Stripe, this will be charged at 1.25% + 20p.';
